import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { getCurrentUser } from '../../../../utils/authSession'
import {
  addJobRemark,
  archiveJob,
  createJob,
  deleteJobAttachment,
  fetchJobPointsPreview,
  fetchTaskOptionsPage,
  getJobRemarks,
  getJobDetail,
  getProjectStakeholdersByTask,
  listJobs,
  listTimesheetsByJob,
  listJobUserOptions,
  uploadJobAttachment,
  updateJob,
  validateJobCompletion,
} from '../api/jobApi'
import { appToast } from '../../../../components/AppToast'
import { deriveJobStatus } from '../../common/pmStatusUtils'

const VALID_STATUSES = ['DRAFT', 'IN_PROGRESS', 'PENDING_REVIEW', 'COMPLETED', 'CLOSED']

function toDateInput(value) {
  if (!value) return ''
  const raw = String(value)
  const datePart = raw.match(/^(\d{4}-\d{2}-\d{2})/)
  if (datePart) return datePart[1]
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ''
  const y = parsed.getFullYear()
  const m = String(parsed.getMonth() + 1).padStart(2, '0')
  const d = String(parsed.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function toDateTimeInput(dateValue, timeValue) {
  if (!dateValue) return undefined
  const time = timeValue && /^\d{2}:\d{2}/.test(String(timeValue))
    ? String(timeValue).slice(0, 5)
    : '00:00'
  return `${dateValue}T${time}:00`
}

function addDays(dateStr, days) {
  if (!dateStr) return ''
  const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return dateStr
  const year = parseInt(match[1], 10)
  const month = parseInt(match[2], 10) - 1
  const day = parseInt(match[3], 10)
  const d = new Date(year, month, day)
  d.setDate(d.getDate() + days)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const r = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${r}`
}

function normalizeText(value) {
  const text = String(value || '').trim()
  return text.length ? text : undefined
}

function normalizeIdList(value) {
  if (!Array.isArray(value)) return []
  return Array.from(
    new Set(
      value
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item) && item > 0),
    ),
  )
}

function normalizeResponsibleDependencies(value) {
  if (!Array.isArray(value)) return []
  const seen = new Set()
  const rows = []
  value.forEach((item, index) => {
    if (!item || typeof item !== 'object') return
    const userId = Number(item.user_id)
    if (!Number.isFinite(userId) || userId <= 0 || seen.has(userId)) return
    seen.add(userId)
    rows.push({
      user_id: userId,
      user_name: String(item.user_name || '').trim() || 'User',
      assignment_note: String(item.assignment_note || ''),
      is_completed: Number(item.is_completed) ? 1 : 0,
      priority_order: Number(item.priority_order) > 0 ? Number(item.priority_order) : index + 1,
    })
  })
  rows.sort((a, b) => Number(a.priority_order || 0) - Number(b.priority_order || 0))
  return rows.map((row, index) => ({ ...row, priority_order: index + 1 }))
}

function clampMinutes(value) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric) || numeric < 0) return 0
  return Math.min(59, Math.floor(numeric))
}

function normalizeHourMinuteInput(value) {
  const text = String(value ?? '').replace(/[^\d]/g, '')
  return text.length ? text : '0'
}

function splitDecimalHours(value) {
  const numeric = Number(value || 0)
  if (!Number.isFinite(numeric) || numeric <= 0) return { hours: '0', minutes: '0' }
  const roundedMinutes = Math.round(numeric * 60)
  const normalizedHours = Math.floor(roundedMinutes / 60)
  const normalizedMinutes = roundedMinutes % 60
  return { hours: String(normalizedHours), minutes: String(normalizedMinutes) }
}

function combineHoursAndMinutes(hoursValue, minutesValue) {
  const hours = Math.max(0, Math.floor(Number(hoursValue || 0) || 0))
  const minutes = clampMinutes(minutesValue)
  const total = hours + minutes / 60
  return Number(total.toFixed(2))
}

function normalizeAttachmentItems(value) {
  if (!Array.isArray(value)) return []
  const seen = new Set()
  const normalized = []
  value.forEach((raw) => {
    let item = null
    if (typeof raw === 'string') {
      const path = String(raw || '').trim()
      if (!path) return
      const fileName = path.split('/').pop()?.split('\\').pop() || path
      item = {
        id: path,
        path,
        originalName: fileName,
        uploadedAt: null,
      }
    } else if (raw && typeof raw === 'object') {
      const path = String(raw.path || '').trim()
      if (!path) return
      const fileName = String(raw.originalName || raw.name || '').trim() || path.split('/').pop()?.split('\\').pop() || path
      item = {
        id: String(raw.id || path),
        path,
        originalName: fileName,
        uploadedAt: raw.uploadedAt || null,
      }
    }
    if (!item) return
    const key = String(item.path || '').toLowerCase()
    if (!key || seen.has(key)) return
    seen.add(key)
    normalized.push(item)
  })
  return normalized
}

function deriveStatus(startDate, isCompleted, currentStatus) {
  return deriveJobStatus(startDate, isCompleted, currentStatus)
}



function stakeholderKey(stakeholder) {
  if (!stakeholder) return ''
  return `${String(stakeholder.role || '').trim().toUpperCase()}|${String(stakeholder.email || '').trim().toLowerCase()}|${String(stakeholder.phone || '').trim()}`
}

export function useJobEntryScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const editingJobId = searchParams.get('id')
  const returnUrl = location.state?.returnTo || '/nerve/job/list'
  const prefillTaskId = searchParams.get('taskId')
  const prefillTaskName = searchParams.get('taskName') || ''
  const prefillTaskCode = searchParams.get('taskCode') || ''
  const prefillProjectName = searchParams.get('projectName') || ''

  const savingRef = useRef(false)

  const storedUser = useMemo(() => getCurrentUser(), [])

  const [form, setForm] = useState({
    task_id: editingJobId ? '' : (prefillTaskId || ''),
    job_name: '',
    assigned_to: editingJobId ? '' : (storedUser.id ? String(storedUser.id) : ''),
    status: 'DRAFT',
    planned_start: editingJobId ? '' : toDateInput(new Date()),
    planned_end: '',
    planned_start_time: '',
    planned_end_time: '',
    planned_hours_value: '0',
    planned_minutes_value: '0',
    completion_note: '',
    notes: '',
    attachments: [],
    dependency_job_ids: [],
    responsible_dependencies: [],
    is_completed: 0,
    rollover_reason: '',
    client_rp_key: '',
  })
  const [jobCode, setJobCode] = useState('')
  const [clientRpOptions, setClientRpOptions] = useState([])

  const [userOptions, setUserOptions] = useState([])
  const [jobOptions, setJobOptions] = useState([])
  const [resolvedProjectId, setResolvedProjectId] = useState('')
  const [isLoading, setIsLoading] = useState(Boolean(editingJobId))
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [optionError, setOptionError] = useState('')
  const [overrunFlag, setOverrunFlag] = useState(null)
  const [timelineMeta, setTimelineMeta] = useState({
    projectName: editingJobId ? '' : (prefillProjectName || ''),
    taskName: editingJobId ? '' : (prefillTaskName || prefillTaskCode || ''),
    plannedStart: '',
    plannedEnd: '',
  })
  const [loggedHours, setLoggedHours] = useState(0)
  const [initialPlannedEnd, setInitialPlannedEnd] = useState('')
  const [initialPlannedStart, setInitialPlannedStart] = useState('')
  const [remarks, setRemarks] = useState([])
  const [isRemarksLoading, setIsRemarksLoading] = useState(false)
  const [isPostingRemark, setIsPostingRemark] = useState(false)
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false)
  const [pointsPreview, setPointsPreview] = useState({ basePoints: 100, mode: 'BASE', netPoints: 100, eventSummary: [] })

  useEffect(() => {
    let active = true

    async function loadOptions() {
      const jobResult = await listJobs({
        page: 1,
        page_size: 100,
        includeTotal: false,
        archive: false,
        fromDate: '1900-01-01',
        toDate: '9999-12-31',
      })
      if (!active) return

      const nextJobs = Array.isArray(jobResult?.items) ? jobResult.items : Array.isArray(jobResult) ? jobResult : []
      setJobOptions(nextJobs)
    }

    async function loadJobIfEditing() {
      if (!editingJobId) return
      const detail = await getJobDetail(editingJobId)
      if (!active) return
      setOverrunFlag(detail?.overrun_flag || null)

      const clientKey = stakeholderKey(detail?.client_rp)
      const isMarkCompleteParam = searchParams.get('mode') === 'markComplete'
      const isCompletedVal = isMarkCompleteParam ? 1 : (Number(detail?.is_completed || 0) ? 1 : 0)
      const statusVal = isMarkCompleteParam ? 'PENDING_REVIEW' : deriveStatus(toDateInput(detail?.planned_start), isCompletedVal, detail?.status)

      setForm({
        task_id: detail?.task_id ? String(detail.task_id) : '',
        job_name: detail?.job_name || '',
        assigned_to: detail?.assigned_to ? String(detail.assigned_to) : '',
        status: statusVal,
        planned_start: toDateInput(detail?.planned_start),
        planned_end: toDateInput(detail?.planned_end),
        planned_start_time: (() => {
          const raw = String(detail?.planned_start || '')
          const m = raw.match(/[T ](\d{2}:\d{2})/)
          return m ? m[1] : ''
        })(),
        planned_end_time: (() => {
          const raw = String(detail?.planned_end || '')
          const m = raw.match(/[T ](\d{2}:\d{2})/)
          return m ? m[1] : ''
        })(),
        ...(() => {
          const splitHours = splitDecimalHours(detail?.planned_hours)
          return {
            planned_hours_value: splitHours.hours,
            planned_minutes_value: splitHours.minutes,
          }
        })(),
        completion_note: detail?.completion_note || '',
        notes: detail?.notes || '',
        attachments: normalizeAttachmentItems(detail?.attachments),
        dependency_job_ids: normalizeIdList(detail?.dependency_job_ids),
        responsible_dependencies: normalizeResponsibleDependencies(
          Array.isArray(detail?.dependency_users) && detail.dependency_users.length
            ? detail.dependency_users
            : normalizeIdList(detail?.dependency_user_ids).map((user_id, index) => ({
                user_id,
                priority_order: index + 1,
                assignment_note: '',
                is_completed: 0,
              })),
        ),
        is_completed: isCompletedVal,
        rollover_reason: '',
        client_rp_key: clientKey,
      })
      setJobCode(detail?.job_code || '')
      setInitialPlannedEnd(toDateInput(detail?.planned_end))
      setInitialPlannedStart(toDateInput(detail?.planned_start))
      setTimelineMeta({
        projectName: detail?.project_name || '',
        taskName: detail?.task_name || '',
        plannedStart: toDateInput(detail?.planned_start),
        plannedEnd: toDateInput(detail?.planned_end),
      })
      setResolvedProjectId(detail?.project_id ? String(detail.project_id) : '')

      const selectedUserIds = normalizeIdList(
        Array.isArray(detail?.dependency_users)
          ? detail.dependency_users.map((row) => Number(row?.user_id)).filter((value) => Number.isFinite(value) && value > 0)
          : detail?.dependency_user_ids,
      )
      if (selectedUserIds.length) {
        try {
          const selectedUsers = await listJobUserOptions({
            page: 1,
            page_size: 200,
            user_ids: selectedUserIds.join(','),
            project_id: Number(detail?.project_id || 0) || undefined,
          })
          if (!active) return
          if (Array.isArray(selectedUsers) && selectedUsers.length) {
            setUserOptions((prev) => {
              const merged = [...(Array.isArray(prev) ? prev : []), ...selectedUsers]
              const map = new Map()
              merged.forEach((user) => {
                if (!user?.id) return
                map.set(String(user.id), user)
              })
              return Array.from(map.values())
            })
          }
        } catch {
          // Ignore hydration failure; UI uses fallback user labels.
        }
      }
    }

    async function bootstrap() {
      setIsLoading(true)
      setError('')
      try {
        await Promise.all([loadOptions(), loadJobIfEditing()])
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load job configuration.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    bootstrap()
    return () => {
      active = false
    }
  }, [editingJobId])

  useEffect(() => {
    let active = true
    async function loadProjectUsers() {
      const projectId = Number(resolvedProjectId || 0)
      try {
        const params = {
          page: 1,
          page_size: 200,
        }
        if (projectId > 0) params.project_id = projectId
        const users = await listJobUserOptions(params)
        if (!active) return
        const loadedUsers = Array.isArray(users) ? users : []
        // Ensure the logged-in user is always in the list so the dropdown & card resolve
        if (storedUser.id && !loadedUsers.some((u) => Number(u.id) === Number(storedUser.id))) {
          loadedUsers.unshift({
            id: storedUser.id,
            name: storedUser.name,
            email: storedUser.email,
            employee_id: storedUser.employee_id,
          })
        }
        setUserOptions(loadedUsers)
      } catch {
        if (!active) return
        setUserOptions([])
      }
    }
    loadProjectUsers()
    return () => {
      active = false
    }
  }, [resolvedProjectId])

  useEffect(() => {
    let active = true
    async function loadPointsPreview() {
      try {
        const payload = await fetchJobPointsPreview(editingJobId ? Number(editingJobId) : undefined)
        if (!active) return
        setPointsPreview({
          basePoints: Number(payload?.basePoints || 100),
          mode: String(payload?.mode || 'BASE'),
          netPoints: Number(payload?.netPoints ?? payload?.basePoints ?? 100),
          eventSummary: Array.isArray(payload?.eventSummary) ? payload.eventSummary : [],
        })
      } catch {
        if (!active) return
        setPointsPreview({ basePoints: 100, mode: 'BASE', netPoints: 100, eventSummary: [] })
      }
    }
    loadPointsPreview()
    return () => {
      active = false
    }
  }, [editingJobId])



  useEffect(() => {
    if (!editingJobId) {
      setRemarks([])
      return
    }
    let active = true
    async function loadRemarks() {
      setIsRemarksLoading(true)
      try {
        const response = await getJobRemarks(editingJobId)
        if (!active) return
        const items = Array.isArray(response?.items) ? response.items : []
        setRemarks(items)
      } catch {
        if (!active) return
        setRemarks([])
      } finally {
        if (active) setIsRemarksLoading(false)
      }
    }
    loadRemarks()
    return () => {
      active = false
    }
  }, [editingJobId])

  useEffect(() => {
    let active = true
    async function loadLoggedHours() {
      if (!editingJobId) {
        setLoggedHours(0)
        return
      }
      try {
        const rows = await listTimesheetsByJob(editingJobId)
        if (!active) return
        const total = rows.reduce((sum, row) => sum + Number(row?.hoursLogged || 0), 0)
        setLoggedHours(Number(total.toFixed(2)))
      } catch {
        if (!active) return
        setLoggedHours(0)
      }
    }
    loadLoggedHours()
    return () => {
      active = false
    }
  }, [editingJobId])

  useEffect(() => {
    let active = true
    async function hydrateStakeholders() {
      if (!form.task_id) {
        setResolvedProjectId('')
        setClientRpOptions([])
        setTimelineMeta((prev) => ({ ...prev, projectName: '', taskName: '' }))
        setForm((prev) => ({ ...prev, client_rp_key: '' }))
        return
      }

      try {
        const result = await getProjectStakeholdersByTask(form.task_id)
        if (!active) return

        const stakeholders = Array.isArray(result?.stakeholders) ? result.stakeholders : []
        setResolvedProjectId(result?.projectId ? String(result.projectId) : '')
        setClientRpOptions(stakeholders)
        setTimelineMeta((prev) => ({
          ...prev,
          projectName: result?.projectName || prev.projectName || '',
          taskName: prev.taskName || '',
        }))

        if (stakeholders.length) {
          const keys = stakeholders.map((row) => stakeholderKey(row))
          setForm((prev) => {
            if (prev.client_rp_key && keys.includes(prev.client_rp_key)) return prev
            return { ...prev, client_rp_key: keys[0] }
          })
        } else {
          setForm((prev) => ({ ...prev, client_rp_key: '' }))
        }
      } catch {
        if (!active) return
        setClientRpOptions([])
      }
    }

    hydrateStakeholders()
    return () => {
      active = false
    }
  }, [form.task_id])

  const completionPercent = useMemo(() => {
    const tracked = [
      form.task_id,
      form.job_name,
      form.assigned_to,
      form.planned_start,
      form.status,
      form.notes,
      form.client_rp_key,
    ]
    const filled = tracked.filter((item) => String(item || '').trim().length > 0).length
    return Math.round((filled / tracked.length) * 100)
  }, [form])

  const selectedClientRp = useMemo(() => {
    return clientRpOptions.find((row) => stakeholderKey(row) === form.client_rp_key) || null
  }, [clientRpOptions, form.client_rp_key])

  const selectedAssignee = useMemo(() => {
    return userOptions.find((row) => Number(row.id) === Number(form.assigned_to)) || null
  }, [form.assigned_to, userOptions])

  const selectedDependencyJobs = useMemo(() => {
    return form.dependency_job_ids
      .map((id) => jobOptions.find((job) => Number(job.id) === Number(id)))
      .filter(Boolean)
  }, [form.dependency_job_ids, jobOptions])


  const attachmentItems = useMemo(() => normalizeAttachmentItems(form.attachments), [form.attachments])

  const estimatedHours = combineHoursAndMinutes(form.planned_hours_value, form.planned_minutes_value)
  const loggedHoursProgress = useMemo(() => {
    if (estimatedHours <= 0 || loggedHours <= 0) return 0
    return Math.min(100, Math.round((loggedHours / estimatedHours) * 100))
  }, [estimatedHours, loggedHours])

  // A rollover only occurs when the job execution day (planned_start date) is
  // postponed to a strictly later calendar date than the originally saved date.
  // Changing only times, keeping the same date, or moving the date earlier
  // does NOT require a rollover reason.
  const isPostponedExecutionDate = Boolean(
    editingJobId &&
      initialPlannedStart &&
      form.planned_start &&
      form.planned_start > initialPlannedStart,
  )

  function patchForm(key, value) {
    setForm((prev) => {
      const next = { ...prev, [key]: value }
      if (key === 'assigned_to') {
        next.is_pool = !value
      }
      return next
    })
  }


  async function toggleCompleted() {
    const next = form.is_completed ? 0 : 1

    // Block toggling ON in creation mode: no timesheets can exist yet
    if (next === 1 && !editingJobId) {
      appToast.error(
        'A new job cannot be marked as completed on creation. ' +
          'Create it first, log timesheets, and have them approved before marking it complete.',
      )
      return
    }

    // Edit mode: call validate endpoint before allowing the toggle
    if (next === 1 && editingJobId) {
      try {
        const validation = await validateJobCompletion(editingJobId)
        if (!validation.valid) {
          const errors = Array.isArray(validation.errors) ? validation.errors : []
          errors.forEach((msg) => appToast.error(msg))
          if (!errors.length) {
            appToast.error('Job cannot be marked as completed. Please check all requirements.')
          }
          return // block the toggle
        }
      } catch {
        // Non-blocking: if validation call fails, allow toggle (server will re-validate on save)
      }
    }

    setForm((prev) => {
      const status = deriveStatus(prev.planned_start, next, prev.status)
      return { ...prev, is_completed: next, status }
    })
  }

  useEffect(() => {
    if (form.planned_start) {
      setForm((prev) => {
        let expectedEnd = prev.planned_start
        if (prev.planned_start_time && prev.planned_end_time) {
          const startTime = String(prev.planned_start_time).slice(0, 5)
          const endTime = String(prev.planned_end_time).slice(0, 5)
          if (endTime < startTime) {
            expectedEnd = addDays(prev.planned_start, 1)
          }
        }
        if (prev.planned_end === expectedEnd) return prev
        return { ...prev, planned_end: expectedEnd }
      })
    }
  }, [form.planned_start, form.planned_start_time, form.planned_end_time])

  useEffect(() => {
    if (editingJobId) return
    if (Number(form.is_completed) === 1) {
      setForm((prev) => (prev.status === 'COMPLETED' ? prev : { ...prev, status: 'COMPLETED' }))
    }
  }, [form.is_completed, editingJobId])

  function addDependencyJob(jobId) {
    const id = Number(jobId)
    if (!id) return
    if (editingJobId && Number(editingJobId) === id) return
    setForm((prev) => ({
      ...prev,
      dependency_job_ids: normalizeIdList([...prev.dependency_job_ids, id]),
    }))
  }

  function removeDependencyJob(jobId) {
    const id = Number(jobId)
    setForm((prev) => ({
      ...prev,
      dependency_job_ids: prev.dependency_job_ids.filter((item) => Number(item) !== id),
    }))
  }

  function addResponsibleUser(userId, userName = 'User') {
    const normalized = Number(userId)
    if (!normalized) return
    setForm((prev) => ({
      ...prev,
      responsible_dependencies: normalizeResponsibleDependencies([
        ...prev.responsible_dependencies,
        {
          user_id: normalized,
          user_name: String(userName || 'User').trim() || 'User',
          priority_order: prev.responsible_dependencies.length + 1,
          assignment_note: '',
          is_completed: 0,
        },
      ]),
    }))
  }

  function removeResponsibleUser(userId) {
    const normalized = Number(userId)
    setForm((prev) => ({
      ...prev,
      responsible_dependencies: normalizeResponsibleDependencies(
        prev.responsible_dependencies.filter((row) => Number(row.user_id) !== normalized),
      ),
    }))
  }

  function updateResponsibleNote(userId, note) {
    const normalized = Number(userId)
    setForm((prev) => ({
      ...prev,
      responsible_dependencies: normalizeResponsibleDependencies(
        prev.responsible_dependencies.map((row) =>
          Number(row.user_id) === normalized
            ? {
                ...row,
                assignment_note: String(note || ''),
              }
            : row,
        ),
      ),
    }))
  }

  function toggleResponsibleCompleted(userId) {
    const normalized = Number(userId)
    setForm((prev) => ({
      ...prev,
      responsible_dependencies: normalizeResponsibleDependencies(
        prev.responsible_dependencies.map((row) =>
          Number(row.user_id) === normalized
            ? {
                ...row,
                is_completed: Number(row.is_completed) ? 0 : 1,
              }
            : row,
        ),
      ),
    }))
  }

  function syncResponsibleUsers(nextUserIds, userNameById = {}) {
    const normalizedIds = normalizeIdList(nextUserIds)
    setForm((prev) => {
      const existingById = new Map(
        normalizeResponsibleDependencies(prev.responsible_dependencies).map((row) => [Number(row.user_id), row]),
      )
      const nextRows = normalizedIds.map((userId, index) => {
        const existing = existingById.get(userId)
        return {
          user_id: userId,
          user_name: String(existing?.user_name || userNameById?.[String(userId)] || 'User').trim() || 'User',
          assignment_note: String(existing?.assignment_note || ''),
          is_completed: Number(existing?.is_completed) ? 1 : 0,
          priority_order: index + 1,
        }
      })
      return {
        ...prev,
        responsible_dependencies: normalizeResponsibleDependencies(nextRows),
      }
    })
  }

  function buildPayload() {
    const plannedHours = combineHoursAndMinutes(form.planned_hours_value, form.planned_minutes_value)
    const dependencyUsers = normalizeResponsibleDependencies(form.responsible_dependencies).map((row, index) => ({
      user_id: Number(row.user_id),
      priority_order: index + 1,
      assignment_note: String(row.assignment_note || '').trim(),
      is_completed: Number(row.is_completed) ? 1 : 0,
    }))
    const dependencyUserIds = dependencyUsers.map((row) => row.user_id)

    return {
      task_id: Number(form.task_id),
      job_name: normalizeText(form.job_name),
      assigned_to: form.assigned_to ? Number(form.assigned_to) : null,
      client_rp: selectedClientRp
        ? {
            role: String(selectedClientRp.role || '').trim().toUpperCase(),
            name: String(selectedClientRp.name || '').trim(),
            email: String(selectedClientRp.email || '').trim().toLowerCase(),
            phone: String(selectedClientRp.phone || '').trim(),
          }
        : undefined,
      planned_hours: plannedHours > 0 ? plannedHours : undefined,
      planned_start: toDateTimeInput(form.planned_start, form.planned_start_time),
      planned_end:   toDateTimeInput(form.planned_end,   form.planned_end_time),
      completion_note: normalizeText(form.completion_note),
      notes: normalizeText(form.notes),
      attachments: normalizeAttachmentItems(form.attachments).map((item) => ({
        id: item.id,
        path: item.path,
        originalName: item.originalName,
        uploadedAt: item.uploadedAt,
      })), 
      is_completed: Number(form.is_completed) ? 1 : 0,
      status: form.status,
      dependency_job_ids: normalizeIdList(form.dependency_job_ids),
      dependency_user_ids: dependencyUserIds,
      dependency_users: dependencyUsers,
      rollover_reason: isPostponedExecutionDate ? normalizeText(form.rollover_reason) : undefined,
    }
  }

  async function handleSave(redirectToAllocate = false) {
    if (savingRef.current) return
    savingRef.current = true
    setError('')
    setSuccess('')
    const estimatedHoursValue = combineHoursAndMinutes(form.planned_hours_value, form.planned_minutes_value)
    if (!form.task_id) {
      setError('Parent task is required.')
      savingRef.current = false
      return
    }
    if (!String(form.job_name || '').trim()) {
      setError('Job name is required.')
      savingRef.current = false
      return
    }
    if (!form.assigned_to && !form.is_pool) {
      setError('Internal RP is required (or select Unassigned / Job Pool).')
      savingRef.current = false
      return
    }
    if (!form.planned_start) {
      setError('Start date is required.')
      savingRef.current = false
      return
    }
    if (!form.planned_end) {
      setError('End date is required.')
      savingRef.current = false
      return
    }
    if (estimatedHoursValue <= 0) {
      setError('Estimated time is required.')
      savingRef.current = false
      return
    }
    if (isPostponedExecutionDate && !String(form.rollover_reason || '').trim()) {
      setError('Rollover reason is required when the job execution date is postponed.')
      savingRef.current = false
      return
    }

    setIsSaving(true)
    try {
      const payload = buildPayload()
      if (editingJobId) {
        await updateJob(editingJobId, payload)
        setSuccess('Job updated successfully.')
      } else {
        const created = await createJob(payload)
        if (created?.job_code) setJobCode(created.job_code)
        setSuccess('Job created successfully.')
      }
      window.setTimeout(() => {
        savingRef.current = false
        setIsSaving(false)
        if (redirectToAllocate === true) {
          navigate('/allocation?mode=real')
        } else {
          navigate(returnUrl)
        }
      }, 250)
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to save job configuration.'))
      savingRef.current = false
      setIsSaving(false)
    }
  }

  function handleSaveAndAllocate() {
    handleSave(true)
  }

  async function handleArchive() {
    if (!editingJobId) return
    if (savingRef.current) return
    setError('')
    setSuccess('')
    savingRef.current = true
    setIsSaving(true)
    try {
      await archiveJob(editingJobId)
      setSuccess('Job archived successfully.')
      window.setTimeout(() => {
        savingRef.current = false
        setIsSaving(false)
        navigate(returnUrl)
      }, 250)
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to archive job.'))
      savingRef.current = false
      setIsSaving(false)
    }
  }

  async function handleUploadAttachment(file) {
    if (!file) return
    if (!editingJobId) {
      setError('Save job first, then upload attachments.')
      return
    }
    setError('')
    setIsUploadingAttachment(true)
    try {
      const result = await uploadJobAttachment(editingJobId, file)
      const attachments = normalizeAttachmentItems(result?.attachments)
      setForm((prev) => ({ ...prev, attachments }))
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to upload attachment.'))
    } finally {
      setIsUploadingAttachment(false)
    }
  }

  async function removeAttachment(attachmentId) {
    const key = String(attachmentId || '').trim()
    if (!key) return
    if (!editingJobId) {
      setForm((prev) => ({
        ...prev,
        attachments: normalizeAttachmentItems(prev.attachments).filter((item) => String(item.id || '') !== key),
      }))
      return
    }
    setError('')
    try {
      const result = await deleteJobAttachment(editingJobId, key)
      setForm((prev) => ({ ...prev, attachments: normalizeAttachmentItems(result?.attachments) }))
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to remove attachment.'))
    }
  }

  async function postRemark(message) {
    if (!editingJobId) return
    setError('')
    setIsPostingRemark(true)
    try {
      const result = await addJobRemark(editingJobId, message)
      const nextItem = result?.item
      if (nextItem) {
        setRemarks((prev) => [...prev, nextItem])
      } else {
        const refreshed = await getJobRemarks(editingJobId)
        setRemarks(Array.isArray(refreshed?.items) ? refreshed.items : [])
      }
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to post remark.'))
    } finally {
      setIsPostingRemark(false)
    }
  }

  function handleDiscard() {
    navigate(returnUrl)
  }

  return {
    state: {
      form,
      jobCode,
      clientRpOptions,
      selectedClientRp,
      selectedAssignee,
      selectedDependencyJobs,
      resolvedProjectId,
      userOptions,
      jobOptions,
      timelineMeta,
      isLoading,
      isSaving,
      error,
      success,
      optionError,
      isEditMode: Boolean(editingJobId),
      editingJobId,
      completionPercent,
      overrunFlag,
      loggedHours,
      loggedHoursProgress,
      attachmentItems,
      isPostponedExecutionDate,
      remarks,
      isRemarksLoading,
      isPostingRemark,
      isUploadingAttachment,
      pointsPreview,
      statuses: VALID_STATUSES,
      isPool: Boolean(form.is_pool || !form.assigned_to),
    },
    actions: {
      patchForm,
      setPlannedHours: (value) => patchForm('planned_hours_value', normalizeHourMinuteInput(value)),
      setPlannedMinutes: (value) => patchForm('planned_minutes_value', String(clampMinutes(value))),
      toggleCompleted,
      addDependencyJob,
      removeDependencyJob,
      addResponsibleUser,
      removeResponsibleUser,
      updateResponsibleNote,
      toggleResponsibleCompleted,
      syncResponsibleUsers,
      handleSave,
      handleSaveAndAllocate,
      handleArchive,
      handleDiscard,
      handleUploadAttachment,
      removeAttachment,
      postRemark,
    },
  }
}
